import React from 'react';
import {createRoot} from 'react-dom/client';
import TagManager from 'react-gtm-module';
import {Provider} from 'react-redux';
import {BrowserRouter} from 'react-router-dom';
import {IntercomProvider} from 'react-use-intercom';
import {ConfigProvider} from 'antd';
import * as Sentry from '@sentry/react';
import {createCache, StyleProvider} from '@ant-design/cssinjs';
import {brandConfig} from './libs/ui/src';
import App from './App';
import {ErrorBoundary} from './ErrorBoundary';
import {store} from './redux';
import {CONSTANTS} from './services/';
import {interceptor} from './services/apiService';
import {init} from './services/sockets';
import * as serviceWorker from './serviceWorker';
import ErrorBoundaryFallback from './components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import {theme} from './theme';

init(store);
interceptor(store);

if (process.env.NX_PUBLIC_SENTRY === 'enabled') {
  Sentry.init({
    dsn: 'https://faf11e8482be3beb278ca2f769ae71a7@o1202726.ingest.us.sentry.io/4509112496947200',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.captureConsoleIntegration({levels: ['error']}),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ['localhost'],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

if (brandConfig.gtmId && process.env.NX_PUBLIC_GTM === 'enabled') {
  TagManager.initialize({
    gtmId: brandConfig.gtmId,
  });
}

const container = document.getElementById('root');
const root = createRoot(container);
const cssCache = createCache();

root.render(
  <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
    <IntercomProvider appId={CONSTANTS.intercomAppId}>
      <Provider store={store}>
        <BrowserRouter>
          <StyleProvider cache={cssCache}>
            <ConfigProvider
              theme={theme}
              // example
              // modal={{
              // className: 'test1',
              // style: {background: 'red'},
              // }}
              // dropdown={{
              //   className: 'dropdown1',
              //   style: {backgroundColor: 'red'},
              // }}
            >
              <App />
            </ConfigProvider>
          </StyleProvider>
        </BrowserRouter>
      </Provider>
    </IntercomProvider>
  </ErrorBoundary>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
